/**
 * Desktop Leave Page — T19, T20, T24
 * Leave requests + balance + form
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Palmtree, Plus, Loader2, CheckCircle2, Clock, XCircle, X, AlertCircle } from 'lucide-react';
import config from 'config';

const API = config.API_URL;

const STATUS_CONFIG = {
  PENDING: { label: 'En attente', icon: Clock, cls: 'text-amber-400 bg-amber-400/10 border-amber-400/20' },
  APPROVED: { label: 'Approuvé', icon: CheckCircle2, cls: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' },
  REJECTED: { label: 'Refusé', icon: XCircle, cls: 'text-red-400 bg-red-400/10 border-red-400/20' },
};

export default function DesktopLeavePage() {
  const { token } = useOutletContext();
  const [data, setData] = useState({ leave_requests: [], balance: {} });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchLeave = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/employees/me/leave`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchLeave(); }, [fetchLeave]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await fetch(`${API}/api/employees/me/leave`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: startDate, end_date: endDate, reason }),
      });
      if (res.ok) {
        setMessage({ ok: true, text: 'Demande de congé envoyée' });
        setShowForm(false);
        setStartDate(''); setEndDate(''); setReason('');
        fetchLeave();
      } else {
        const err = await res.json();
        setMessage({ ok: false, text: err.detail || 'Erreur' });
      }
    } catch {
      setMessage({ ok: false, text: 'Erreur de connexion' });
    }
    setSubmitting(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" /></div>;

  const bal = data.balance || {};

  return (
    <div className="p-8 max-w-5xl" data-testid="desktop-leave-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Mes congés</h1>
          <p className="text-white/40 text-sm mt-1">Gérez vos demandes de congés et consultez votre solde</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#C9A24D] text-black text-sm font-semibold hover:bg-[#d4af5a] transition" data-testid="desktop-new-leave-btn">
          <Plus className="w-4 h-4" /> Nouvelle demande
        </button>
      </div>

      {/* T24: Leave balance */}
      <div className="grid grid-cols-3 gap-5 mb-8" data-testid="desktop-leave-balance">
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5">
          <p className="text-white/30 text-xs font-medium uppercase tracking-wider mb-2">Congés annuels</p>
          <p className="text-3xl font-bold text-white">{bal.vacation_days_total ?? 25}<span className="text-base text-white/30 ml-1">jours</span></p>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5">
          <p className="text-white/30 text-xs font-medium uppercase tracking-wider mb-2">Jours pris</p>
          <p className="text-3xl font-bold text-amber-400">{bal.vacation_days_used ?? 0}<span className="text-base text-white/30 ml-1">jours</span></p>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5">
          <p className="text-white/30 text-xs font-medium uppercase tracking-wider mb-2">Jours restants</p>
          <p className="text-3xl font-bold text-emerald-400">{bal.vacation_days_remaining ?? 25}<span className="text-base text-white/30 ml-1">jours</span></p>
        </div>
      </div>

      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-xl text-sm mb-6 ${message.ok ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
          {message.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Leave form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end" data-testid="desktop-leave-form">
          <div>
            <label className="text-white/60 text-xs font-medium mb-1.5 block">Date de début</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm" data-testid="desktop-leave-start" />
          </div>
          <div>
            <label className="text-white/60 text-xs font-medium mb-1.5 block">Date de fin</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm" data-testid="desktop-leave-end" />
          </div>
          <div>
            <label className="text-white/60 text-xs font-medium mb-1.5 block">Motif</label>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Congés payés, RTT..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-white/20" data-testid="desktop-leave-reason" />
          </div>
          <div className="md:col-span-3 flex gap-3">
            <button type="submit" disabled={submitting} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#C9A24D] text-black text-sm font-semibold disabled:opacity-40 transition" data-testid="desktop-leave-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? 'Envoi...' : 'Envoyer la demande'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 text-white/40 text-sm">Annuler</button>
          </div>
        </form>
      )}

      {/* Leave requests table */}
      {data.leave_requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl bg-white/[0.02] border border-white/5">
          <Palmtree className="w-14 h-14 text-white/10 mb-4" />
          <p className="text-white/25 text-sm">Aucune demande de congé</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/5 overflow-hidden" data-testid="desktop-leave-table">
          <table className="w-full">
            <thead>
              <tr className="bg-white/[0.02]">
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Période</th>
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Motif</th>
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Statut</th>
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Demandé le</th>
              </tr>
            </thead>
            <tbody>
              {data.leave_requests.map(l => {
                const cfg = STATUS_CONFIG[l.status] || STATUS_CONFIG.PENDING;
                const Icon = cfg.icon;
                return (
                  <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02] transition" data-testid={`desktop-leave-row-${l.id}`}>
                    <td className="px-5 py-4 text-white text-sm font-medium">
                      {new Date(l.start_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} — {new Date(l.end_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-5 py-4 text-white/40 text-sm">{l.reason || '-'}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border ${cfg.cls}`}>
                        <Icon className="w-3.5 h-3.5" /> {cfg.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-white/30 text-sm">{new Date(l.created_at).toLocaleDateString('fr-FR')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
